<template>
  <div class="method-step" :class="{ 'is-new': isNewStep }">
    <!-- New Step Input -->
    <div v-if="isNewStep" class="method-step-new">
      <button class="add-row-btn" @click="addStep">
        <b-icon icon="plus" size="is-small" />
        Add step
      </button>
    </div>

    <!-- Existing Step -->
    <div v-else>
      <!-- Header row: drag handle, delete button -->
      <div class="method-step-header">
        <b-icon
          class="drag-handle step-drag"
          icon="drag"
          size="is-small"
        />
        <button class="step-action-btn " title="Delete" @click="removeStep">
          <b-icon icon="delete" size="is-small" />
        </button>
      </div>

      <!-- Step content with number inline -->
      <div class="method-step-main">
        <span class="step-number">{{ index }}</span>
        <div class="step-content">
          <editor
            v-model.trim="step.sentence"
            class="step-textarea"
            placeholder="e.g. Heat the oven to 180C..."
            @input="updateStep"
          />
          <div class="step-ingredients">
            <div class="step-ingredients-list">
              <draggable
                v-model="step.ingredients"
                draggable=".step-ingredient-tag"
                class="step-ingredients-draggable"
                @update="updateStep">
                <span
                  v-for="(item, idx) in mappedIngredients"
                  :key="item.ingredient.id"
                  class="step-ingredient-tag">
                  <i v-if="item.ingredient.optional" class="optional-marker">(Opt) </i>
                  {{ formatAmount(item.ingredient.amount, item.ratio) }} {{ item.ingredient.unit }} <strong>{{ item.ingredient.name }}</strong>
                  <span class="tag-remove" @click="removeIngredient(idx)">&times;</span>
                </span>
              </draggable>
              <button class="step-ingredient-add" @click="openIngredientModal">
                <b-icon icon="plus" size="is-small" />
                Add ingredient
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Tips Section -->
      <div class="step-tips" v-if="step.tips && step.tips.length > 0">
        <draggable
          v-model="step.tips"
          draggable=".step-tip-item"
          handle=".tip-drag-handle"
          ghostClass="drag-ghost"
          dragClass="drag-item"
          @update="updateStep">
          <div v-for="(tip, idx) in step.tips" :key="tip.id" class="step-tip-item">
            <div class="step-tip-wrapper">
              <svg class="tip-drag-handle step-tip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
              </svg>
              <editor
                v-model.trim="tip.value"
                class="step-tip-input"
                placeholder="Add an optional tip for this step..."
                @input="updateStep"
              />
              <button class="tip-remove-btn" @click="removeTip(tip, idx)">
                <b-icon icon="delete" size="is-small" />
              </button>
            </div>
          </div>
        </draggable>
      </div>

      <!-- Add Tip - Always visible input -->
      <div class="step-tip-add">
        <div class="step-tip-wrapper add-new">
          <svg class="step-tip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
          </svg>
          <input
            type="text"
            v-model.trim="newTipValue"
            class="new-tip-input"
            placeholder="Add a tip..."
            @keyup.enter="addTipFromInput"
          />
          <button
            v-if="newTipValue"
            class="tip-add-btn"
            @click="addTipFromInput"
            title="Add tip">
            <b-icon icon="plus" size="is-small" />
          </button>
        </div>
      </div>
    </div>

    <AddStepIngredientModal v-if="false" />
  </div>
</template>

<script>
import AddStepIngredientModal from "@/components/modals/AddStepIngredientModal"
import { v4 as uuidv4 } from 'uuid';
import Draggable from 'vuedraggable'
import Editor from "@/components/BaseEditor"
import debounce from "lodash/debounce"

export default {
  name: 'EditStep',
  components: {
    AddStepIngredientModal,
    Draggable,
    Editor,
  },
  props: [
    "stepIn",
    "isNewStep",
    "index",
    "ingredients"
  ],
  data() {
    return {
      step: null,
      newTipValue: ''
    }
  },
  created() {
    this.step = this.stepIn ?? { id: null, sentence: null, tips: [], ingredients:[] }
  },
  computed: {
    mappedIngredients() {
      if (!this.step.ingredients) return [];
      return this.step.ingredients.map(stepIngredient => {
        const found = this.ingredients.find(item => item.id === stepIngredient.id);
        return { ingredient: found || {}, ratio: stepIngredient.ratio }
      }).filter(item => item.ingredient.id);
    }
  },
  methods: {
    formatAmount(amount, ratio) {
      const value = amount * ratio;
      return parseFloat(value.toFixed(2));
    },
    addStep() {
      this.step.id = uuidv4();
      this.$emit('add-step', this.step);
      this.step = {id: null, sentence: null, tips:[], ingredients:[]};
    },
    removeStep() {
      this.$emit('remove-step', this.step);
    },
    updateStep: debounce(function () {
      this.$emit('update-step', this.step);
    }, 1000),

    // Ingredients
    openIngredientModal() {
      this.$buefy.modal.open({
          parent: this,
          component: AddStepIngredientModal,
          hasModalCard: true,
          trapFocus: true,
          props: {
            ingredients: this.ingredients
          },
          events: {
            'add-ingredient': value => {
              this.addIngredient(value.ingredient, value.ratio)
            }
          }
      })
    },
    addIngredient(ingredient, ratio) {
      const alreadyUsed = this.step.ingredients.map((i) => { return i.id } ).indexOf(ingredient.id) >= 0;
      if (!alreadyUsed){
        this.step.ingredients.push({id: ingredient.id, ratio: ratio});
        this.$emit('update-step', this.step);
      }else {
        this.$buefy.toast.open({type: 'is-danger', message: `That ingredient is already used on this step!`});
      }
    },
    removeIngredient(index) {
      this.$delete(this.step.ingredients, index);
      this.$emit('update-step', this.step);
    },

    // Tips
    addTipFromInput() {
      if (this.newTipValue && this.newTipValue.trim()) {
        const newTip = {
          id: uuidv4(),
          value: this.newTipValue.trim()
        };
        if (!this.step.tips) {
          this.step.tips = [];
        }
        this.step.tips.push(newTip);
        this.$emit('update-step', this.step);
        this.newTipValue = '';
      }
    },
    removeTip(tip, index) {
      this.$buefy.dialog.confirm({
          title: 'Deleting Tip',
          message: `Are you sure you want to delete this tip?<br /><br /><strong>This action cannot be undone!</strong>`,
          confirmText: 'Delete Tip',
          type: 'is-danger',
          hasIcon: true,
          onConfirm: () => {
            this.$delete(this.step.tips, index);
            this.$emit('update-step', this.step);
          }
      });
    },
  }
}
</script>

<style scoped>
.method-step {
  background: var(--bg-tertiary);
  border-radius: var(--radius-md);
  overflow: hidden;
  border: 1px solid var(--border-light);
  transition: border-color 0.15s;
}

.method-step:hover {
  border-color: var(--border-medium);
}

.method-step.is-new {
  background: transparent;
  border: none;
}

/* Header row with drag handle and delete */
.method-step-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  border-bottom: 1px solid var(--border-light);
  background: rgba(0,0,0,0.02);
}

.step-drag {
  color: var(--text-muted);
  cursor: grab;
  flex-shrink: 0;
}

.step-drag:active {
  cursor: grabbing;
}

.method-step-main {
  display: flex;
  gap: 12px;
  padding: 14px;
  align-items: flex-start;
}

.step-number {
  width: 28px;
  height: 28px;
  background: var(--feeds-green-gradient);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(0, 133, 122, 0.3);
}

.step-content {
  flex: 1;
  min-width: 0;
}

.step-textarea {
  width: 100%;
}

.step-textarea :deep(.ProseMirror) {
  min-height: 60px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-sm);
  padding: 12px;
  font-size: 14px;
}

.step-textarea :deep(.ProseMirror:focus) {
  border-color: var(--feeds-green);
  box-shadow: 0 0 0 4px var(--feeds-green-light);
}

.step-ingredients {
  margin-top: 12px;
}

.step-ingredients-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.step-ingredients-draggable {
  display: contents;
}

.step-ingredient-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: var(--feeds-green-light);
  color: var(--feeds-green);
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: grab;
}

.step-ingredient-tag:active {
  cursor: grabbing;
}

.step-ingredient-tag .optional-marker {
  font-style: italic;
  opacity: 0.8;
}

.step-ingredient-tag strong {
  font-weight: 600;
}

.step-ingredient-tag .tag-remove {
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.15s;
  font-size: 16px;
  line-height: 1;
}

.step-ingredient-tag .tag-remove:hover {
  opacity: 1;
}

.step-ingredient-add {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  background: transparent;
  border: 1px dashed var(--border-medium);
  color: var(--text-muted);
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
}

.step-ingredient-add:hover {
  border-color: var(--feeds-green);
  color: var(--feeds-green);
  background: var(--feeds-green-light);
}

.step-action-btn {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: all 0.15s;
}

.step-action-btn:hover {
  background: var(--bg-secondary);
  color: var(--text-secondary);
}

.step-action-btn.delete:hover {
  background: #fef2f2;
  color: #dc2626;
}

/* Tips Section */
.step-tips {
  padding: 0 14px 8px 54px;
}

.step-tip-item {
  margin-bottom: 4px;
}

.step-tip-add {
  padding: 12px 14px 12px 54px;
  border-top: 1px solid var(--border-light);
}

.step-tip-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.step-tip-wrapper.add-new {
  padding: 8px 10px;
  border: 1px dashed var(--border-light);
  border-radius: var(--radius-sm);
}

.step-tip-wrapper.add-new:focus-within {
  border-style: solid;
  border-color: var(--feeds-orange);
  background: #fff8f5;
}

.step-tip-icon {
  width: 20px;
  height: 20px;
  color: var(--feeds-orange);
  flex-shrink: 0;
  margin-top: 2px;
}

.step-tip-icon.tip-drag-handle {
  cursor: grab;
}

.step-tip-icon.tip-drag-handle:active {
  cursor: grabbing;
}

.step-tip-input {
  flex: 1;
  min-width: 0;
}

.step-tip-input :deep(.ProseMirror) {
  border: none;
  padding: 0;
  min-height: 20px;
  font-size: 13px;
  background: transparent;
  box-shadow: none;
}

.step-tip-input :deep(.ProseMirror:focus) {
  box-shadow: none;
  border: none;
}

.new-tip-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  font-family: inherit;
  color: var(--text-primary);
}

.new-tip-input::placeholder {
  color: var(--text-muted);
  font-style: italic;
}

.add-tip-text {
  flex: 1;
  color: var(--text-muted);
  font-size: 13px;
  font-style: italic;
}

.tip-remove-btn,
.tip-add-btn {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: all 0.15s;
  flex-shrink: 0;
}

.tip-remove-btn:hover {
  background: #fef2f2;
  color: #dc2626;
}

.tip-add-btn:hover {
  background: var(--feeds-green-light);
  color: var(--feeds-green);
}

/* Add button styles */
.add-row-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: transparent;
  border: 2px dashed var(--border-medium);
  border-radius: var(--radius-md);
  color: var(--text-muted);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  justify-content: center;
}

.add-row-btn:hover {
  border-color: var(--feeds-green);
  color: var(--feeds-green);
  background: var(--feeds-green-light);
}
</style>
